/**
 * eSIM Admin Dashboard — Mantine UI with full data surfacing
 */

import React, { useState, useEffect } from 'react';
import {
  SimpleGrid, Paper, Group, Stack, Text, Title, Badge, Progress,
  Modal, Card, Button, CopyButton, Tooltip, ActionIcon, Tabs,
  Table, Image, Anchor, Loader, Center
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { authFetch } from '../../auth';
import PurchaseEsimModal from './PurchaseEsimModal';
import './EsimDashboard.css';

const ADMIN_EMAIL = 'nfodor@mac.com';

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return '0 MB';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return gb % 1 === 0 ? `${gb} GB` : `${gb.toFixed(1)} GB`;
  return `${Math.round(bytes / (1024 * 1024))} MB`;
};

const statusColor = (status) => {
  if (!status) return 'gray';
  if (status === 'IN_USE') return 'blue';
  if (status === 'GOT_RESOURCE' || status === 'RELEASED') return 'green';
  if (status.includes('EXPIRED')) return 'gray';
  return 'yellow';
};

const usagePct = (used, total) => {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.round((used / total) * 100));
};

// ── Detail Modal ──
const EsimDetailModal = ({ esim, opened, onClose }) => {
  const [pinVisible, setPinVisible] = useState(false);
  const [pukVisible, setPukVisible] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');

  if (!esim) return null;

  const pct = usagePct(esim.dataUsed, esim.totalVolume);

  const CopyRow = ({ label, value }) => {
    if (!value) return null;
    return (
      <Group justify="space-between" py={4}>
        <Text size="sm" c="dimmed" w={120}>{label}</Text>
        <Group gap={6} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all', flex: 1 }}>{value}</Text>
          <CopyButton value={value}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                <ActionIcon size="sm" variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy}>
                  <span style={{ fontSize: 14 }}>{copied ? '\u2713' : '\u2398'}</span>
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
      </Group>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <Text fw={600}>{esim.packageName || esim.packageCode || 'eSIM Details'}</Text>
          <Badge color={statusColor(esim.esimStatus)} size="sm">{esim.esimStatus}</Badge>
        </Group>
      }
      size="lg"
      fullScreen={isMobile}
      styles={{ body: { maxHeight: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 200px)', overflowY: 'auto' } }}
    >
      <Stack gap="md">
        {/* QR Code */}
        {esim.qrCodeUrl && (
          <Center>
            <Paper withBorder p="md" radius="md">
              <Image src={esim.qrCodeUrl} alt="eSIM QR Code" w={200} h={200} fit="contain" />
            </Paper>
          </Center>
        )}

        {/* White-label link */}
        {esim.shortUrl && (
          <Paper withBorder p="sm" radius="md" bg="blue.0">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">White-label Install Link</Text>
                <Anchor href={esim.shortUrl} target="_blank" size="sm" ff="monospace">
                  {esim.shortUrl}
                </Anchor>
              </Stack>
              <Group gap={4}>
                <CopyButton value={esim.shortUrl}>
                  {({ copied, copy }) => (
                    <Button size="xs" variant={copied ? 'filled' : 'light'} color={copied ? 'teal' : 'blue'} onClick={copy}>
                      {copied ? 'Copied' : 'Copy Link'}
                    </Button>
                  )}
                </CopyButton>
                <Button size="xs" variant="light" component="a" href={esim.shortUrl} target="_blank" rel="noopener noreferrer">
                  Open
                </Button>
              </Group>
            </Group>
          </Paper>
        )}

        {/* Activation Code */}
        {esim.activationCode && (
          <Paper withBorder p="sm" radius="md" bg="green.0">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">Activation Code</Text>
                <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>{esim.activationCode}</Text>
              </Stack>
              <CopyButton value={esim.activationCode}>
                {({ copied, copy }) => (
                  <Button size="xs" variant={copied ? 'filled' : 'light'} color={copied ? 'teal' : 'green'} onClick={copy}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Paper>
        )}

        {/* Data Usage */}
        <Paper withBorder p="sm" radius="md">
          <Text size="xs" c="dimmed" mb={4}>Data Usage</Text>
          <Group justify="space-between" mb={4}>
            <Text size="sm">{formatBytes(esim.dataUsed)} used</Text>
            <Text size="sm" c="dimmed">{formatBytes(esim.totalVolume)} total</Text>
          </Group>
          <Progress value={pct} size="lg" color={pct > 90 ? 'red' : pct > 70 ? 'yellow' : 'blue'} radius="xl" />
          <Text size="xs" c="dimmed" ta="right" mt={2}>{pct}%</Text>
        </Paper>

        {/* Identifiers */}
        <Paper withBorder p="sm" radius="md">
          <Text size="xs" fw={600} c="dimmed" mb={4}>Identifiers</Text>
          <CopyRow label="ICCID" value={esim.iccid} />
          <CopyRow label="IMSI" value={esim.imsi} />
          <CopyRow label="EID" value={esim.eid} />
          <CopyRow label="MSISDN" value={esim.msisdn} />
          <CopyRow label="Order #" value={esim.orderNo} />
        </Paper>

        {/* Security */}
        {(esim.pin || esim.puk) && (
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" fw={600} c="dimmed" mb={4}>Security Codes</Text>
            {esim.pin && (
              <Group justify="space-between" py={4}>
                <Text size="sm" c="dimmed" w={120}>PIN</Text>
                <Group gap={6}>
                  <Text size="sm" ff="monospace">{pinVisible ? esim.pin : '\u2022\u2022\u2022\u2022'}</Text>
                  <Button size="compact-xs" variant="subtle" onClick={() => setPinVisible(!pinVisible)}>
                    {pinVisible ? 'Hide' : 'Reveal'}
                  </Button>
                </Group>
              </Group>
            )}
            {esim.puk && (
              <Group justify="space-between" py={4}>
                <Text size="sm" c="dimmed" w={120}>PUK</Text>
                <Group gap={6}>
                  <Text size="sm" ff="monospace">{pukVisible ? esim.puk : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'}</Text>
                  <Button size="compact-xs" variant="subtle" onClick={() => setPukVisible(!pukVisible)}>
                    {pukVisible ? 'Hide' : 'Reveal'}
                  </Button>
                </Group>
              </Group>
            )}
          </Paper>
        )}

        {/* Network & Dates */}
        <Paper withBorder p="sm" radius="md">
          <Text size="xs" fw={600} c="dimmed" mb={4}>Network & Dates</Text>
          {esim.apn && (
            <Group justify="space-between" py={4}>
              <Text size="sm" c="dimmed">APN</Text>
              <Text size="sm">{esim.apn}</Text>
            </Group>
          )}
          {esim.speed && (
            <Group justify="space-between" py={4}>
              <Text size="sm" c="dimmed">Speed</Text>
              <Text size="sm">{esim.speed}</Text>
            </Group>
          )}
          {esim.ipExport && (
            <Group justify="space-between" py={4}>
              <Text size="sm" c="dimmed">IP Export</Text>
              <Text size="sm">{esim.ipExport}</Text>
            </Group>
          )}
          {esim.smdpStatus && (
            <Group justify="space-between" py={4}>
              <Text size="sm" c="dimmed">SM-DP+ Status</Text>
              <Badge size="sm" color="violet" variant="light">{esim.smdpStatus}</Badge>
            </Group>
          )}
          {esim.activateTime && (
            <Group justify="space-between" py={4}>
              <Text size="sm" c="dimmed">Activated</Text>
              <Text size="sm">{new Date(esim.activateTime).toLocaleString()}</Text>
            </Group>
          )}
          {esim.installationTime && (
            <Group justify="space-between" py={4}>
              <Text size="sm" c="dimmed">Installed</Text>
              <Text size="sm">{new Date(esim.installationTime).toLocaleString()}</Text>
            </Group>
          )}
          {esim.expiredTime && (
            <Group justify="space-between" py={4}>
              <Text size="sm" c="dimmed">Expires</Text>
              <Text size="sm">{new Date(esim.expiredTime).toLocaleString()}</Text>
            </Group>
          )}
        </Paper>

        {/* Package History */}
        {esim.packageList && esim.packageList.length > 0 && (
          <Paper withBorder p="sm" radius="md">
            <Text size="xs" fw={600} c="dimmed" mb={4}>Package History</Text>
            {esim.packageList.map((pkg, i) => (
              <Group key={i} justify="space-between" py={4} style={{ borderBottom: i < esim.packageList.length - 1 ? '1px solid var(--mantine-color-gray-2)' : 'none' }}>
                <Stack gap={0}>
                  <Text size="sm">{pkg.packageName || pkg.packageCode}</Text>
                  {pkg.locationCode && <Text size="xs" c="dimmed">{pkg.locationCode}</Text>}
                </Stack>
                {pkg.createTime && <Text size="xs" c="dimmed">{new Date(pkg.createTime).toLocaleDateString()}</Text>}
              </Group>
            ))}
          </Paper>
        )}

        {/* Action Buttons */}
        <Group justify="center" mt="sm">
          {esim.qrCodeUrl && (
            <Button variant="light" component="a" href={esim.qrCodeUrl} target="_blank" rel="noopener noreferrer">
              Download QR
            </Button>
          )}
          {esim.shortUrl && (
            <Button variant="light" color="teal" component="a" href={esim.shortUrl} target="_blank" rel="noopener noreferrer">
              Open Install Link
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

// ── eSIM Card ──
const EsimCard = ({ esim, onClick }) => {
  const pct = usagePct(esim.dataUsed, esim.totalVolume);
  const isExpired = esim.esimStatus?.includes('EXPIRED');

  return (
    <Card
      shadow="sm"
      padding="md"
      radius="md"
      withBorder
      style={{ cursor: 'pointer', opacity: isExpired ? 0.65 : 1, transition: 'box-shadow 0.15s' }}
      onClick={onClick}
      className="esim-card-hover"
    >
      <Group justify="space-between" mb="xs">
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} size="sm" truncate="end">
            {esim.packageName || esim.packageCode || 'Unknown Package'}
          </Text>
          {esim.locationCode && (
            <Text size="xs" c="dimmed">{esim.locationCode}</Text>
          )}
        </Stack>
        <Badge color={statusColor(esim.esimStatus)} size="sm" variant="light">
          {esim.esimStatus || 'Unknown'}
        </Badge>
      </Group>

      <Stack gap={4} mb="xs">
        <Group justify="space-between">
          <Text size="xs" c="dimmed">{formatBytes(esim.dataUsed)} / {formatBytes(esim.totalVolume)}</Text>
          <Text size="xs" c="dimmed">{pct}%</Text>
        </Group>
        <Progress value={pct} size="sm" color={pct > 90 ? 'red' : pct > 70 ? 'yellow' : 'blue'} radius="xl" />
      </Stack>

      <Group justify="space-between">
        <Text size="xs" ff="monospace" c="dimmed">
          {esim.iccid ? esim.iccid.slice(0, 12) + '...' : 'No ICCID'}
        </Text>
        <Text size="xs" c="dimmed">
          {esim.expiredTime ? new Date(esim.expiredTime).toLocaleDateString() : 'No expiry'}
        </Text>
      </Group>
    </Card>
  );
};

// ── Provider eSIMs Tab ──
const ProviderEsimsTab = ({ esims }) => {
  const [selectedEsim, setSelectedEsim] = useState(null);

  if (esims.length === 0) {
    return (
      <Center py={60}>
        <Stack align="center" gap="sm">
          <Text size="xl">No Provider eSIMs</Text>
          <Text c="dimmed" size="sm">No eSIMs found in the provider account.</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {esims.map((esim, idx) => (
          <EsimCard key={idx} esim={esim} onClick={() => setSelectedEsim(esim)} />
        ))}
      </SimpleGrid>
      <EsimDetailModal
        esim={selectedEsim}
        opened={!!selectedEsim}
        onClose={() => setSelectedEsim(null)}
      />
    </>
  );
};

// ── Orders Tab ──
const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await authFetch('/api/esim/admin/orders');
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Center py={60}><Loader size="md" /></Center>;
  }

  if (orders.length === 0) {
    return (
      <Center py={60}>
        <Stack align="center" gap="sm">
          <Text size="xl">No Local Orders Yet</Text>
          <Text c="dimmed" size="sm" maw={400} ta="center">
            Orders placed through the dashboard will appear here. See the Provider eSIMs tab for order history.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Table.ScrollContainer minWidth={700}>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Order #</Table.Th>
            <Table.Th>User</Table.Th>
            <Table.Th>Package</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Wholesale</Table.Th>
            <Table.Th>Retail</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Date</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {orders.map((order, idx) => (
            <Table.Tr key={idx}>
              <Table.Td><Text size="sm" ff="monospace">{order.order_no || order.transaction_id || '-'}</Text></Table.Td>
              <Table.Td><Text size="sm">{order.customer_email || order.admin_email || '-'}</Text></Table.Td>
              <Table.Td>{order.package_code || order.package_name || '-'}</Table.Td>
              <Table.Td>{order.provider || 'Provider'}</Table.Td>
              <Table.Td>${order.wholesale_price?.toFixed(2) || '-'}</Table.Td>
              <Table.Td>${order.retail_price?.toFixed(2) || '-'}</Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color={
                  order.status === 'ready' || order.status === 'completed' || order.status === 'provisioned_via_webhook' ? 'green' :
                  order.status === 'created' ? 'yellow' :
                  order.status === 'archived' ? 'gray' : 'blue'
                }>
                  {order.status}
                </Badge>
              </Table.Td>
              <Table.Td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

// ── User eSIM View (non-admin) ──
const UserEsimView = ({ user }) => {
  const [esims, setEsims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedEsim, setSelectedEsim] = useState(null);

  useEffect(() => {
    loadUserEsims();
    // Check for purchase success in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('purchase') === 'success') {
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Reload after a brief delay to let webhook provision
      setTimeout(loadUserEsims, 2000);
    }
  }, []);

  const loadUserEsims = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/esim/my/esims');
      if (response.ok) {
        const data = await response.json();
        setEsims(data.esims || []);
      } else {
        setError('Failed to load your eSIMs');
      }
    } catch (err) {
      setError('Network error loading eSIMs');
      console.error('User eSIMs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = () => {
    setShowPurchaseModal(false);
    loadUserEsims();
  };

  // Map order data to EsimCard-compatible format
  const mapOrderToEsim = (order) => {
    const esimData = order.esim_data || {};
    return {
      packageName: order.package_name || esimData.packageName || order.package_code,
      packageCode: order.package_code,
      esimStatus: esimData.esimStatus || order.status,
      dataUsed: esimData.dataUsed || 0,
      totalVolume: esimData.totalVolume || 0,
      iccid: esimData.iccid,
      imsi: esimData.imsi,
      eid: esimData.eid,
      msisdn: esimData.msisdn,
      orderNo: order.order_no,
      qrCodeUrl: esimData.qrCodeUrl,
      shortUrl: esimData.shortUrl,
      activationCode: esimData.activationCode,
      pin: esimData.pin,
      puk: esimData.puk,
      apn: esimData.apn,
      speed: esimData.speed,
      ipExport: esimData.ipExport,
      smdpStatus: esimData.smdpStatus,
      activateTime: esimData.activateTime,
      installationTime: esimData.installationTime,
      expiredTime: esimData.expiredTime,
      locationCode: esimData.locationCode,
      packageList: esimData.packageList,
      // Extra fields for user context
      _createdAt: order.created_at,
      _retailPrice: order.retail_price,
      _status: order.status
    };
  };

  if (loading) {
    return (
      <div className="esim-dashboard">
        <Center py={80}><Loader size="lg" /></Center>
      </div>
    );
  }

  if (error) {
    return (
      <div className="esim-dashboard">
        <Paper withBorder p="xl" radius="md" bg="red.0">
          <Stack align="center" gap="sm">
            <Title order={4} c="red">Unable to load eSIMs</Title>
            <Text c="dimmed">{error}</Text>
            <Button variant="light" color="red" onClick={loadUserEsims}>Try Again</Button>
          </Stack>
        </Paper>
      </div>
    );
  }

  return (
    <div className="esim-dashboard">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Stack gap={2}>
          <Title order={2}>My eSIMs</Title>
          <Text size="sm" c="dimmed">{user.email}</Text>
        </Stack>
        <Button
          size="sm"
          variant="gradient"
          gradient={{ from: 'indigo', to: 'violet' }}
          onClick={() => setShowPurchaseModal(true)}
        >
          + Browse eSIM Plans
        </Button>
      </Group>

      {esims.length === 0 ? (
        /* Empty state */
        <Center py={80}>
          <Stack align="center" gap="md" maw={400}>
            <Paper withBorder p="xl" radius="md" ta="center">
              <Text size="xl" fw={600} mb="xs">No eSIMs yet</Text>
              <Text c="dimmed" size="sm" mb="lg">
                Browse data plans from 200+ countries and get connected instantly. No physical SIM needed.
              </Text>
              <Button
                variant="gradient"
                gradient={{ from: 'indigo', to: 'violet' }}
                size="md"
                onClick={() => setShowPurchaseModal(true)}
              >
                Browse Data Plans
              </Button>
            </Paper>
          </Stack>
        </Center>
      ) : (
        /* eSIM Cards */
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {esims.map((order, idx) => {
            const esim = mapOrderToEsim(order);
            return (
              <EsimCard key={idx} esim={esim} onClick={() => setSelectedEsim(esim)} />
            );
          })}
        </SimpleGrid>
      )}

      {/* Detail Modal */}
      <EsimDetailModal
        esim={selectedEsim}
        opened={!!selectedEsim}
        onClose={() => setSelectedEsim(null)}
      />

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <PurchaseEsimModal
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={handlePurchaseSuccess}
          user={user}
        />
      )}
    </div>
  );
};

// ── Main Dashboard (admin) ──
const AdminDashboard = ({ user }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await authFetch('/api/esim/admin/dashboard');
      if (response.ok) {
        const data = await response.json();
        setDashboard(data.dashboard);
      } else {
        setError('Failed to load dashboard');
      }
    } catch (err) {
      setError('Network error loading dashboard');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = () => {
    setShowPurchaseModal(false);
    loadDashboard();
  };

  const handleClearCache = async () => {
    try {
      await authFetch('/api/esim/admin/cache/clear', { method: 'POST' });
      loadDashboard();
    } catch (err) {
      console.error('Error clearing cache:', err);
    }
  };

  if (loading) {
    return (
      <div className="esim-dashboard">
        <Center py={80}><Loader size="lg" /></Center>
      </div>
    );
  }

  if (error) {
    return (
      <div className="esim-dashboard">
        <Paper withBorder p="xl" radius="md" bg="red.0">
          <Stack align="center" gap="sm">
            <Title order={4} c="red">Unable to load dashboard</Title>
            <Text c="dimmed">{error}</Text>
            <Button variant="light" color="red" onClick={loadDashboard}>Try Again</Button>
          </Stack>
        </Paper>
      </div>
    );
  }

  const providerEsims = dashboard?.providerEsims || [];
  const balance = dashboard?.balance;

  return (
    <div className="esim-dashboard">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Stack gap={2}>
          <Title order={2}>eSIM Admin Dashboard</Title>
          <Badge variant="light" color="gray" size="sm">Admin: {user.email}</Badge>
        </Stack>
        <Group gap="sm">
          <Button variant="default" size="sm" onClick={handleClearCache}>
            Refresh Cache
          </Button>
          <Button
            size="sm"
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            onClick={() => setShowPurchaseModal(true)}
          >
            + Purchase eSIM
          </Button>
        </Group>
      </Group>

      <Tabs defaultValue="overview">
        <Tabs.List mb="md">
          <Tabs.Tab value="overview">Overview</Tabs.Tab>
          <Tabs.Tab value="esims">Provider eSIMs ({providerEsims.length})</Tabs.Tab>
          <Tabs.Tab value="orders">Orders ({dashboard?.orderCount || 0})</Tabs.Tab>
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview">
          {dashboard && (
            <Stack gap="md">
              {/* Stats Row */}
              <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="md">
                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Balance</Text>
                  <Text size="lg" fw={600}>
                    {balance && !balance.error
                      ? `$${balance.usd != null ? balance.usd.toFixed(2) : '?.??'}`
                      : 'N/A'}
                  </Text>
                </Paper>

                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Packages</Text>
                  <Text size="lg" fw={600}>{(dashboard.totalPackages || 0).toLocaleString()}</Text>
                </Paper>

                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Countries</Text>
                  <Text size="lg" fw={600}>{dashboard.countriesCount || 0}</Text>
                </Paper>

                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Margin</Text>
                  <Text size="lg" fw={600}>{((dashboard.margin || 0) * 100).toFixed(0)}%</Text>
                </Paper>

                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Provider eSIMs</Text>
                  <Text size="lg" fw={600}>{dashboard.providerEsimCount || 0}</Text>
                </Paper>
              </SimpleGrid>

              {/* Price Range */}
              {dashboard.priceRange && (
                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>Price Range (Retail)</Text>
                  <Text size="sm">${dashboard.priceRange.min?.toFixed(2)} - ${dashboard.priceRange.max?.toFixed(2)}</Text>
                </Paper>
              )}

              {/* Top Countries */}
              {dashboard.topCountries && dashboard.topCountries.length > 0 && (
                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={8}>Top Countries</Text>
                  <Group gap="xs">
                    {dashboard.topCountries.map(c => (
                      <Badge key={c.code} variant="light" color="indigo" size="md">
                        {c.flag} {c.name} ({c.packageCount})
                      </Badge>
                    ))}
                  </Group>
                </Paper>
              )}

              {/* Recent Provider eSIMs */}
              {providerEsims.length > 0 && (
                <Paper withBorder p="md" radius="md">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={8}>Recent Provider eSIMs</Text>
                  <Stack gap={0}>
                    {providerEsims.slice(0, 5).map((esim, idx) => (
                      <Group key={idx} justify="space-between" py={6}
                        style={{ borderBottom: idx < Math.min(providerEsims.length, 5) - 1 ? '1px solid var(--mantine-color-gray-2)' : 'none' }}>
                        <Text size="sm" fw={500}>{esim.packageName || esim.packageCode || 'N/A'}</Text>
                        <Group gap="sm">
                          <Badge size="xs" color={statusColor(esim.esimStatus)} variant="light">
                            {esim.esimStatus}
                          </Badge>
                          <Text size="xs" c="dimmed">
                            {formatBytes(esim.dataUsed)} / {formatBytes(esim.totalVolume)}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {esim.expiredTime ? new Date(esim.expiredTime).toLocaleDateString() : ''}
                          </Text>
                        </Group>
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </Tabs.Panel>

        {/* Provider eSIMs Tab */}
        <Tabs.Panel value="esims">
          <ProviderEsimsTab esims={providerEsims} />
        </Tabs.Panel>

        {/* Orders Tab */}
        <Tabs.Panel value="orders">
          <OrdersTab />
        </Tabs.Panel>
      </Tabs>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <PurchaseEsimModal
          onClose={() => setShowPurchaseModal(false)}
          onSuccess={handlePurchaseSuccess}
          user={user}
        />
      )}
    </div>
  );
};

// ── Route Component ──
// /esim — always user view for everyone
const EsimDashboard = ({ user }) => {
  if (!user) {
    return (
      <div className="esim-dashboard">
        <Center py={80}><Loader size="lg" /></Center>
      </div>
    );
  }

  return <UserEsimView user={user} />;
};

export { AdminDashboard };
export default EsimDashboard;
